import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "components/bootstrap";
import { RUNTIME_STATUS_FILTER, FAILED_MESSAGE, SETUP_MESSAGE, STOPPED_MESSAGE } from "components/inputs/Constants";
import NotificationBanner from "components/inputs/NotificationBanner";
import { useStore } from "stores/connect";
import { useQueryParams, ArrayParam, NumberParam, StringParam } from "routing/QueryParams";
import ForwarderInputStatesStore, { ForwarderInputStatesActions } from "../stores/ForwarderInputStatesStore";
import useDisconnectedForwarders from "../hooks/useDisconnectedForwarders";
const FAILED_STATES = ["FAILED", "FAILING", "INVALID_CONFIGURATION"];
const INITIALIZING_STATES = ["CREATED", "INITIALIZED", "STARTING"];
const STOPPED_STATES = ["STOPPED", "TERMINATED"];
const hasInputInState = (states, targetStates) => states.some((state) => targetStates.includes(state.state));
const ForwarderInputsNotifications = () => {
  const { t } = useTranslation("enterprise_forwarder");
  const { forwarderInputStates: states } = useStore(ForwarderInputStatesStore);
  const [, setQueryParams] = useQueryParams({
    filters: ArrayParam,
    page: NumberParam,
    slice: StringParam,
    sliceCol: StringParam
  });
  const { disconnectedForwarderIds } = useDisconnectedForwarders();
  useEffect(() => {
    ForwarderInputStatesActions.register();
    return () => ForwarderInputStatesActions.unregister();
  }, []);
  const items = useMemo(() => {
    if (!states) return [];
    const result = [];
    if (hasInputInState(states, FAILED_STATES)) {
      result.push({
        id: "failed",
        severity: "danger",
        message: /* @__PURE__ */ React.createElement(React.Fragment, null, FAILED_MESSAGE, " ", /* @__PURE__ */ React.createElement(
          Button,
          {
            bsStyle: "link",
            onClick: () => {
              setQueryParams({
                filters: [`${RUNTIME_STATUS_FILTER}=FAILED`],
                page: 1,
                slice: void 0,
                sliceCol: void 0
              });
            }
          },
          t("forwarder_inputs_notifications.show_failed")
        ), ".")
      });
    }
    if (hasInputInState(states, INITIALIZING_STATES)) {
      result.push({
        id: "setup",
        severity: "warning",
        message: /* @__PURE__ */ React.createElement(React.Fragment, null, SETUP_MESSAGE, " ", /* @__PURE__ */ React.createElement(
          Button,
          {
            bsStyle: "link",
            onClick: () => {
              setQueryParams({
                filters: [`${RUNTIME_STATUS_FILTER}=SETUP`],
                page: 1,
                slice: void 0,
                sliceCol: void 0
              });
            }
          },
          t("forwarder_inputs_notifications.show_setup")
        ), ".")
      });
    }
    if (hasInputInState(states, STOPPED_STATES)) {
      result.push({
        id: "stopped",
        severity: "warning",
        message: /* @__PURE__ */ React.createElement(React.Fragment, null, STOPPED_MESSAGE, " ", /* @__PURE__ */ React.createElement(
          Button,
          {
            bsStyle: "link",
            onClick: () => {
              setQueryParams({
                filters: [`${RUNTIME_STATUS_FILTER}=NOT_RUNNING`],
                page: 1,
                slice: void 0,
                sliceCol: void 0
              });
            }
          },
          t("forwarder_inputs_notifications.show_stopped")
        ), ".")
      });
    }
    if (disconnectedForwarderIds.length > 0) {
      result.push({
        id: "disconnected",
        severity: "warning",
        message: /* @__PURE__ */ React.createElement(React.Fragment, null, t("forwarder_inputs_notifications.disconnected_message"), " ", /* @__PURE__ */ React.createElement(
          Button,
          {
            bsStyle: "link",
            onClick: () => {
              setQueryParams({
                filters: disconnectedForwarderIds.map((id) => `forwarder_id=${id}`),
                page: 1,
                slice: void 0,
                sliceCol: void 0
              });
            }
          },
          t("forwarder_inputs_notifications.show_disconnected")
        ), ".")
      });
    }
    return result;
  }, [disconnectedForwarderIds, setQueryParams, states, t]);
  return /* @__PURE__ */ React.createElement(NotificationBanner, { title: t("forwarder_inputs_notifications.warning"), items });
};
export default ForwarderInputsNotifications;
